import { useMemo, useState } from 'react';
import Graphic from '@arcgis/core/Graphic';
import Point from '@arcgis/core/geometry/Point';
import Polygon from '@arcgis/core/geometry/Polygon';
import { StylishNewCheckbox } from 'components/DesignSystems/New/StylishNewCheckbox';
import { IconButton } from 'components/DesignSystems/buttons/IconButton';
import { useActualIncidentId } from 'components/IAP/hooks/commonHooks';
import {
  useIncidentAreas,
  type IncidentArea,
} from 'components/IAP/hooks/incidentAreaHooks';
import { FeatureItem } from './FeatureItem';
import { Layer, PropertyRow } from './Layer';
import { useUscgMapContext } from './UscgMapContext';
import { INCIDENT_AREA_LAYER_TITLE } from './utils/useIncidentAreaLayerSync';

/** Only areas someone drew can be zoomed to; the rest exist on the form alone. */
function areaGraphic(area: IncidentArea): Graphic | null {
  const geometry = area.geometry;
  if (!geometry) return null;
  if (geometry.type === 'Point') {
    const [longitude, latitude] = geometry.coordinates;
    return new Graphic({ geometry: new Point({ longitude, latitude }) });
  }
  if (geometry.type === 'Polygon') {
    return new Graphic({
      geometry: new Polygon({ rings: geometry.coordinates }),
    });
  }
  if (geometry.type === 'MultiPolygon') {
    return new Graphic({
      geometry: new Polygon({ rings: geometry.coordinates.flat() }),
    });
  }
  return null;
}

/**
 * The "215A Incident Areas" row in the layers panel.
 *
 * The layer has no stored definition, so there is nothing to review, version or
 * delete here: it is a view of the areas on the safety analysis card, and the
 * only controls it needs are visibility and zoom.
 */
export function IncidentAreaLayerItem() {
  const { mapView, isIncidentAreaVisible, toggleIncidentAreaVisibility } =
    useUscgMapContext();
  const incidentId = useActualIncidentId();
  const { data: areas = [], isLoading } = useIncidentAreas(incidentId);
  const [searchTerm, setSearchTerm] = useState('');

  const located = useMemo(
    () => areas.filter((area) => area.geometry != null),
    [areas]
  );

  const filtered = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return located;
    return located.filter((area) =>
      [area.areaName, area.hazard, area.mitigations, area.riskGain]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(term)
    );
  }, [located, searchTerm]);

  const zoomToLayer = async () => {
    if (!mapView || located.length === 0) return;
    if (!isIncidentAreaVisible) toggleIncidentAreaVisibility();
    const graphics = located
      .map(areaGraphic)
      .filter((graphic): graphic is Graphic => graphic != null);
    if (graphics.length === 0) return;
    await mapView.goTo(graphics, { duration: 500 });
  };

  const zoomToArea = async (area: IncidentArea) => {
    const graphic = areaGraphic(area);
    if (!mapView || !graphic) return;
    if (!isIncidentAreaVisible) toggleIncidentAreaVisibility();
    await mapView.goTo(graphic, { duration: 500 });
  };

  return (
    <Layer.Item
      eventKey="incident-area-layer"
      header={
        <div className="d-flex justify-content-between align-items-center w-100">
          <div className="d-flex align-items-center gap-2">
            <StylishNewCheckbox
              className="mt-0"
              checked={isIncidentAreaVisible}
              onClick={(e) => {
                e.stopPropagation();
                toggleIncidentAreaVisibility();
              }}
            />
            <span className="fw-semibold">{INCIDENT_AREA_LAYER_TITLE}</span>
          </div>
          <IconButton
            iconProps={{ iconName: 'map' }}
            onClick={(e) => {
              e.stopPropagation();
              void zoomToLayer();
            }}
            aria-label="Zoom to incident areas"
            title="Zoom to incident areas"
            disabled={located.length === 0}
          />
        </div>
      }
    >
      <Layer.Content>
        <PropertyRow label="Areas on the map" value={String(located.length)} />
        <Layer.FeaturesList
          isLoading={isLoading}
          searchTerm={searchTerm}
          onChangeSearchTerm={setSearchTerm}
          showSearch
          showAddObjectButton={false}
        >
          {filtered.length === 0 ? (
            <p className="small text-muted mb-0 p-2 pt-0">
              {located.length === 0
                ? 'No incident areas have been given a location. Draw one on the ICS 215A.'
                : 'No areas match this search.'}
            </p>
          ) : (
            <ul className="list-unstyled d-grid mb-0 gap-2">
              {filtered.map((area) => (
                <FeatureItem
                  key={area.areaId}
                  title={area.areaName}
                  externalFeature={{ featureId: area.areaId ?? '' }}
                  onZoom={() => zoomToArea(area)}
                >
                  <PropertyRow label="Hazards / Risks" value={area.hazard} />
                  <PropertyRow label="Mitigations" value={area.mitigations} />
                  <PropertyRow label="Risk/Gain" value={area.riskGain ?? ''} />
                </FeatureItem>
              ))}
            </ul>
          )}
        </Layer.FeaturesList>
      </Layer.Content>
    </Layer.Item>
  );
}
